import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from './authService';
import { API_BASE_URL } from '../utils/constants';

const HistoryPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('tringAuthToken');
    const fetchOrderHistory = async () => {
      try {
        const historyResponse = await fetchWithAuth(
          `${API_BASE_URL}/menu/orders/user/my`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setOrders(historyData);
        } else {
          console.error('Failed to fetch order history');
        }
      } catch (error) {
        console.error('Error fetching order history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderHistory();
  }, []);

  const formatDate = dateString => {
    if (!dateString) return 'N/A';

    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getStatusColor = status => {
    switch (status.toLowerCase()) {
      case 'completed':
        return '#22C55E';
      case 'pending':
        return '#F59E0B';
      case 'cancelled':
        return '#EF4444';
      case 'preparing':
        return '#3B82F6';
      default:
        return '#6B7280';
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        paddingBottom: '80px',
        maxWidth: '500px',
        margin: '0 auto',
      }}
    >
      <div
        style={{
          padding: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <ArrowLeft
            size={24}
            onClick={() => navigate(-1)}
            style={{ cursor: 'pointer' }}
          />
          <h1
            style={{
              marginLeft: '16px',
              fontSize: '20px',
              fontWeight: '600',
              margin: 0,
            }}
          >
            Order History
          </h1>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p>Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              marginTop: '16px',
            }}
          >
            <p>No order history found</p>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {orders.map(order => (
              <div
                key={order.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div>
                    <h3 style={{ margin: '0 0 8px 0' }}>
                      Order #{order.id.slice(-6)}
                    </h3>
                    <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                      {formatDate(order.createdAt)}
                    </p>
                  </div>
                  <div
                    style={{
                      backgroundColor: getStatusColor(order.status),
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'capitalize',
                    }}
                  >
                    {order.status.toLowerCase()}
                  </div>
                </div>

                <div style={{ margin: '16px 0' }}>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Table:</strong> {order.tableNumber || 'N/A'}
                  </p>

                  {order.notes && (
                    <p style={{ margin: '4px 0', fontStyle: 'italic' }}>
                      <strong>Notes:</strong> {order.notes}
                    </p>
                  )}
                </div>

                <div
                  style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}
                >
                  <h4 style={{ margin: '0 0 8px 0' }}>Items:</h4>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    {order.items.map((item, index) => (
                      <li key={index} style={{ marginBottom: '8px' }}>
                        <div style={{ textAlign: 'left' }}>
                          <strong>
                            {item.quantity}x {item.itemName}
                          </strong>
                          <div style={{ marginLeft: '8px', color: '#666' }}>
                            ₹
                            {item.totalPrice?.toFixed(2) ||
                              item.unitPrice?.toFixed(2) ||
                              '0.00'}
                          </div>
                        </div>
                        {item.specialInstructions && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#666',
                              marginTop: '4px',
                              fontStyle: 'italic',
                            }}
                          >
                            Note: {item.specialInstructions}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {order.approvedAt && (
                  <div
                    style={{
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #eee',
                      fontSize: '12px',
                      color: '#666',
                    }}
                  >
                    <p style={{ margin: '4px 0' }}>
                      <strong>Approved by:</strong> {order.approvedBy || 'N/A'}
                    </p>
                    <p style={{ margin: '4px 0' }}>
                      <strong>Approved at:</strong>{' '}
                      {formatDate(order.approvedAt)}
                    </p>
                    {order.completedAt && (
                      <p style={{ margin: '4px 0' }}>
                        <strong>Completed at:</strong>{' '}
                        {formatDate(order.completedAt)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
